import { ReactNode, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, theme, Dropdown, Modal, Input } from "antd";
import { SwapOutlined, EditOutlined, MinusCircleOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { CustomPopover } from "../common";
import { MediaGroup } from "../../api/mediaGroupAPI";
import { GroupCounts, GroupFilter, UNGROUPED_FILTER } from "./mediaGroup";
import MediaGroupBar, { GroupCandidate } from "./MediaGroupBar";
import MediaGroupAssignModal, { GroupAssignResult } from "./modal/MediaGroupAssignModal";

interface ThumbnailItem {
  key: string;
  thumbnailUrl: string;
  /** 동영상 항목: 썸네일 이미지가 없으면(thumbnailUrl 빈 값) 이 동영상의 첫 프레임을 보여준다 */
  videoUrl?: string;
  name: string;
  qcResult?: string | null;
  /** 미디어 식별자 (slideId / photoId / grossPhotoId) — 그룹 배정 대상 */
  mediaId?: number;
  /** 소속 그룹 id (mediaGroupId). null 이면 그룹없음 */
  mediaGroupId?: number | null;
  /** @deprecated 구 슬라이드 문자열 그룹. 신규 그룹 UI 는 mediaGroupId 를 쓴다 */
  groupId?: string | null;
  /** @deprecated mediaId 로 대체 */
  slideId?: number;
}

type SizeMode = "default" | "large";

interface MediaThumbnailGridProps {
  title: string;
  items: ThumbnailItem[];
  currentIndex: number;
  isActive: boolean;
  hasEditAuth: boolean;
  onSelect: (index: number) => void;
  /** 없으면 Edit 버튼을 숨긴다 */
  onOpenEditModal?: () => void;
  onOpenAddModal: () => void;
  /** Add 버튼 오른쪽에 붙는 추가 버튼 */
  extraActions?: ReactNode;
  size?: SizeMode;
  /**
   * 헤더에 title 을 그릴지 여부. 탭 패널처럼 탭 라벨이 이미 종류를 알려주는 곳에서는 false.
   * (title 자체는 "Add {title}" 버튼 문구에 계속 쓰이므로 비우지 않는다)
   */
  showTitle?: boolean;
  // 이름 수정 (optional — 없으면 우클릭 "이름 수정" 미노출). index 는 items 기준 원본 인덱스
  onRename?: (originalIndex: number, newName: string) => Promise<void> | void;

  // ── 미디어 그룹 (optional — groups 가 없으면 그룹 UI 전체 미노출) ──
  groups?: MediaGroup[];
  groupCounts?: GroupCounts;
  activeGroup?: GroupFilter;
  onGroupFilterChange?: (next: GroupFilter) => void;
  itemLabel?: string; // "슬라이드" | "이미지" | "그로스 이미지"
  onCreateGroup?: (name: string) => Promise<void>;
  onRenameGroup?: (mediaGroupId: number, name: string) => Promise<void>;
  onDeleteGroup?: (mediaGroupId: number) => Promise<void>;
  onMoveToGroup?: (
    mediaIds: number[],
    targetGroupId: number | null,
    newGroupName?: string
  ) => Promise<void>;
  /** 그룹 편집(멤버 전체 교체). 체크된 목록이 그 그룹의 새 멤버가 된다 */
  onEditGroupMembers?: (mediaGroupId: number, mediaIds: number[]) => Promise<void>;
}

interface ThumbnailWrapperProps {
  $primaryColor: string;
  $borderColor: string;
  $isActive: boolean;
  $size: SizeMode;
  $isFailed: boolean;
}

const MediaThumbnailGrid = ({
  title,
  items,
  currentIndex,
  isActive,
  hasEditAuth,
  onSelect,
  onOpenEditModal,
  onOpenAddModal,
  extraActions,
  size = "default",
  showTitle = true,
  onRename,
  groups,
  groupCounts,
  activeGroup = null,
  onGroupFilterChange,
  itemLabel = "미디어",
  onCreateGroup,
  onRenameGroup,
  onDeleteGroup,
  onMoveToGroup,
  onEditGroupMembers,
}: MediaThumbnailGridProps) => {
  const { token } = theme.useToken();
  const addLabel = title.replace(" List", "");

  // 단건 그룹 지정 (우클릭 → 그룹 지정). 다중 선택 배정은 Edit 모달에서.
  const [groupMoveTarget, setGroupMoveTarget] = useState<number | null>(null);

  // 이름 수정 모달 상태
  const [renameTarget, setRenameTarget] = useState<{ index: number; currentName: string } | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const submitRename = async () => {
    if (!renameTarget || !onRename) return;
    const name = renameValue.trim();
    if (!name) return;
    setIsRenaming(true);
    try {
      await onRename(renameTarget.index, name);
      setRenameTarget(null);
    } finally {
      setIsRenaming(false);
    }
  };

  const showGroupUI = !!groups && !!onGroupFilterChange;

  // 그룹 개수는 상위(Post)에서 case 필터까지 반영해 계산해 내려주지만,
  // 넘어오지 않으면 현재 items 기준으로 센다.
  const counts: GroupCounts = useMemo(() => {
    if (groupCounts) return groupCounts;
    const byGroup: Record<number, number> = {};
    let ungrouped = 0;
    items.forEach((it) => {
      const gid = it.mediaGroupId ?? null;
      if (gid == null) ungrouped += 1;
      else byGroup[gid] = (byGroup[gid] ?? 0) + 1;
    });
    return { total: items.length, ungrouped, byGroup };
  }, [groupCounts, items]);

  // 필터 적용된 목록
  const visibleWithOriginalIndex = useMemo(() => {
    const withIndex = items.map((item, originalIndex) => ({ item, originalIndex }));
    if (!showGroupUI || activeGroup == null) return withIndex;
    if (activeGroup === UNGROUPED_FILTER) {
      return withIndex.filter(({ item }) => (item.mediaGroupId ?? null) == null);
    }
    return withIndex.filter(({ item }) => item.mediaGroupId === activeGroup);
  }, [items, activeGroup, showGroupUI]);

  // 그룹 편집 모달에 띄울 목록. 현재 탭에 보이는(= case 필터가 적용된) 미디어 전체.
  const groupCandidates: GroupCandidate[] = useMemo(
    () =>
      items
        .filter((it) => it.mediaId != null)
        .map((it) => ({
          mediaId: it.mediaId!,
          name: it.name,
          thumbnailUrl: it.thumbnailUrl,
          mediaGroupId: it.mediaGroupId ?? null,
        })),
    [items]
  );

  const handleGroupFilterChange = (next: GroupFilter) => {
    if (!onGroupFilterChange) return;
    onGroupFilterChange(next);
    const firstOriginalIndex = items.findIndex((it) => {
      if (next == null) return true;
      const gid = it.mediaGroupId ?? null;
      return next === UNGROUPED_FILTER ? gid == null : gid === next;
    });
    if (firstOriginalIndex !== -1) onSelect(firstOriginalIndex);
  };

  return (
    <SectionWrapper>
      {/* 그룹 바가 없으면(동영상 탭 등) 그룹 바의 아래 여백만큼 헤더에 간격을 준다 */}
      <SectionHeader $gapBelow={!showGroupUI}>
        {showTitle && title && <span>{title}</span>}

        {showGroupUI && (
          <MediaGroupBar
            groups={groups!}
            counts={counts}
            active={activeGroup}
            onChange={handleGroupFilterChange}
            hasEditAuth={hasEditAuth}
            onCreate={onCreateGroup}
            onRename={onRenameGroup}
            onDelete={onDeleteGroup}
            itemLabel={itemLabel}
            editableItems={groupCandidates}
            onEditMembers={onEditGroupMembers}
          />
        )}

        {(hasEditAuth || extraActions) && (
          <ButtonGroup>
            {hasEditAuth && onOpenEditModal && (
              <Button size="small" variant="outlined" onClick={onOpenEditModal}>
                Edit
              </Button>
            )}
            {hasEditAuth && (
              <Button size="small" variant="solid" color="primary" onClick={onOpenAddModal}>
                Add {addLabel}
              </Button>
            )}
            {extraActions}
          </ButtonGroup>
        )}
      </SectionHeader>

      <ThumbnailGrid $size={size}>
        {visibleWithOriginalIndex.map(({ item, originalIndex }) => {
          const canChangeGroup = hasEditAuth && !!onMoveToGroup && item.mediaId != null;
          const canRename = hasEditAuth && !!onRename;
          const menuItems: MenuProps["items"] = [
            ...(canRename
              ? [
                  {
                    key: "rename",
                    label: "이름 수정",
                    icon: <EditOutlined />,
                    onClick: () => {
                      setRenameTarget({ index: originalIndex, currentName: item.name ?? "" });
                      setRenameValue(item.name ?? "");
                    },
                  },
                ]
              : []),
            ...(canChangeGroup
              ? [
                  {
                    key: "changeGroup",
                    label: "그룹 지정",
                    icon: <SwapOutlined />,
                    onClick: () => setGroupMoveTarget(item.mediaId!),
                  },
                ]
              : []),
            // 그룹에 속해 있을 때만 해제 메뉴를 띄운다
            ...(canChangeGroup && (item.mediaGroupId ?? null) != null
              ? [
                  {
                    key: "clearGroup",
                    label: "그룹 해제",
                    icon: <MinusCircleOutlined />,
                    onClick: () => onMoveToGroup!([item.mediaId!], null),
                  },
                ]
              : []),
          ];

          const wrapper = (
            <ThumbnailWrapper
              key={item.key}
              $size={size}
              $primaryColor={token.colorPrimary}
              $borderColor={token.colorBorder}
              $isActive={isActive && originalIndex === currentIndex}
              $isFailed={item.qcResult === 'FAIL'}
              onClick={() => onSelect(originalIndex)}
            >
              {!item.thumbnailUrl && item.videoUrl ? (
                // 동영상 썸네일이 없을 때: 첫 프레임(메타데이터만 미리 받음)
                <ThumbnailImage
                  as="video"
                  src={`${item.videoUrl}#t=0.1`}
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <ThumbnailImage
                  src={item.thumbnailUrl}
                  alt={`${addLabel} ${originalIndex + 1} - ${item.key}`}
                />
              )}
              <CustomPopover title={item.name}>
                <ThumbnailName $size={size}>{item.name || "\u00A0"}</ThumbnailName>
              </CustomPopover>
            </ThumbnailWrapper>
          );

          return menuItems.length > 0 ? (
            <Dropdown key={item.key} menu={{ items: menuItems }} trigger={["contextMenu"]}>
              {wrapper}
            </Dropdown>
          ) : wrapper;
        })}
      </ThumbnailGrid>

      <MediaGroupAssignModal
        open={groupMoveTarget != null}
        groups={groups ?? []}
        selectedCount={1}
        itemLabel={itemLabel}
        allowUngroup={false}
        currentGroupId={
          items.find((it) => it.mediaId === groupMoveTarget)?.mediaGroupId ?? null
        }
        onCancel={() => setGroupMoveTarget(null)}
        onSubmit={async ({ targetGroupId, newGroupName }: GroupAssignResult) => {
          if (groupMoveTarget == null || !onMoveToGroup) return;
          await onMoveToGroup([groupMoveTarget], targetGroupId, newGroupName);
          setGroupMoveTarget(null);
        }}
      />

      <Modal
        title="이름 수정"
        open={!!renameTarget}
        onCancel={() => setRenameTarget(null)}
        onOk={submitRename}
        confirmLoading={isRenaming}
        okText="수정"
        cancelText="취소"
        centered
        destroyOnClose
      >
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={submitRename}
          placeholder="이름"
          autoFocus
          maxLength={255}
        />
      </Modal>
    </SectionWrapper>
  );
};

export type { ThumbnailItem, MediaThumbnailGridProps };
export default MediaThumbnailGrid;

const SectionWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const SectionHeader = styled.div<{ $gapBelow: boolean }>`
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* 그룹 바(MediaGroupBar)의 margin-bottom 과 같은 간격 */
  margin-bottom: ${({ $gapBelow }) => ($gapBelow ? "8px" : "0")};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  /* 앞에 그룹 바·제목이 없어도 항상 오른쪽 끝 */
  margin-left: auto;
`;

const ThumbnailGrid = styled.div<{ $size: SizeMode }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${({ $size }) => ($size === "large" ? "90px" : "80px")}, 1fr));
  gap: 8px;
  max-height: ${({ $size }) => ($size === "large" ? "260px" : "180px")};
  overflow-y: auto;
`;

const ThumbnailWrapper = styled.div<ThumbnailWrapperProps>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  ${({ $size }) =>
    $size === "large"
      ? `& > img { min-height: 60px; }`
      : "aspect-ratio: 1;"}
  padding: ${({ $size }) => ($size === "large" ? "0.2rem" : "0.3rem")};
  border-radius: 4px;
  overflow: hidden;
  border: ${({ $isActive, $isFailed, $primaryColor, $borderColor }) =>
    $isActive
      ? `2px solid ${$primaryColor}`
      : $isFailed
      ? `1.5px solid #fdd7d7`
      : `1px solid ${$borderColor}`};
  transition: border 0.2s;

  &:hover {
    border-color: ${({ $isFailed, $primaryColor }) => $isFailed ? '#ff4d4f' : $primaryColor};
    opacity: 0.85;
  }
`;

const ThumbnailName = styled.span<{ $size: SizeMode }>`
  flex-shrink: 0;
  text-align: center;
  overflow: hidden;

  ${({ $size }) =>
    $size === "large"
      ? `
        font-size: 0.9rem;
        line-height: 1.3;
        white-space: nowrap;
        text-overflow: ellipsis;
      `
      : `
        font-size: 0.75rem;
        line-height: 1.2;
        white-space: nowrap;
        text-overflow: ellipsis;
      `}
`;

const ThumbnailImage = styled.img`
  width: 100%;
  flex: 1;
  object-fit: contain;
  display: block;
  min-height: 0;
`;
